/**
 * @spec docs/specs/features/wallet-relation-api.md#ui-requirements
 * @spec docs/specs/features/wallet-relations-replay-protection.md
 * Hook for managing wallet relations with EIP-712 signing
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelectedWallet } from './useSelectedWallet';
import {
  queryLinkedWallets,
  linkWallets as apiLinkWallets,
  unlinkWallet as apiUnlinkWallet,
  setWalletLabel as apiSetWalletLabel,
  getWalletNonces,
  canonicalOrder,
} from '../lib/wallet-relations';
import { WALLET_RELATIONS_EIP712_DOMAIN, EIP712_TYPES, type LinkedWallet } from '@/types';
import { prepareWallet } from '@/lib/wallet-actions/prepare';
import {
  EIP712_DOMAIN_NAME_VERSION,
  signTypedData as signPreparedTypedData,
} from '@/lib/wallet-actions/sign';
import { ACTIONS } from '@/lib/wallet-actions/actions';

/**
 * Result of signing an unlink request (includes signature and replay protection params)
 */
export interface SignUnlinkResult {
  signature: string;
  timestamp: number;
  nonce: number;
}

/**
 * Return type for useWalletRelations hook
 */
export interface UseWalletRelationsReturn {
  /** List of linked wallets */
  linkedWallets: LinkedWallet[];
  /** Whether the initial query is loading */
  isLoading: boolean;
  /** Error message if any operation failed */
  error: string | null;
  /** Whether the connected wallet is a Privy embedded wallet */
  isPrivyEmbeddedWallet: boolean;
  /** Whether wallet can use linking features (only Privy embedded wallets) */
  canLinkWallets: boolean;
  /** Whether the linked wallets have been fetched (user has opted in) */
  hasFetched: boolean;
  /** Fetch linked wallets (requires signing - triggers 2FA if enabled) */
  fetchLinkedWallets: () => Promise<void>;
  /** Refresh linked wallets list */
  refetch: () => Promise<void>;
  /** Link another wallet to the current wallet */
  linkWallet: (otherWalletAddress: string, otherWalletSignature: string) => Promise<boolean>;
  /** Unlink current wallet from all relations */
  unlinkAll: () => Promise<boolean>;
  /** Sign a link request for the current wallet */
  signLinkRequest: (otherWalletAddress: string) => Promise<string | null>;
  /** Sign an unlink request for the current wallet (returns signature with timestamp and nonce) */
  signUnlinkRequest: () => Promise<SignUnlinkResult | null>;
  /** Rename a linked wallet (sets label via signed API call) */
  renameWallet: (targetAddress: string, newLabel: string) => Promise<boolean>;
}

/**
 * Hook for managing wallet relations
 *
 * Provides:
 * - Query linked wallets (with automatic signing via TanStack Query)
 * - Link wallets (requires signatures from both wallets)
 * - Unlink wallet (removes all relations)
 * - Privy wallet type detection (only Privy embedded wallets can link)
 *
 * Uses TanStack Query for automatic fetching, caching, and cache invalidation.
 */
export function useWalletRelations(): UseWalletRelationsReturn {
  const queryClient = useQueryClient();
  const { wallet, address } = useSelectedWallet();

  // Track if user has opted in to fetching (to avoid auto-triggering 2FA popup)
  const [hasFetched, setHasFetched] = useState(false);

  // Reset hasFetched when the selected wallet address changes.
  // Without this, switching wallets (radio dot) would auto-trigger signing
  // for the new wallet because hasFetched was true from the previous wallet.
  const prevAddressRef = useRef(address);
  useEffect(() => {
    if (prevAddressRef.current && address && prevAddressRef.current !== address) {
      setHasFetched(false);
    }
    prevAddressRef.current = address;
  }, [address]);

  // Check if it's a Privy embedded wallet (walletClientType === 'privy')
  const isPrivyEmbeddedWallet = wallet?.walletClientType === 'privy';
  const canLinkWallets = !!wallet;

  /**
   * Sign typed data using the connected wallet
   * Returns null if signing fails (does not throw)
   */
  const signTypedData = useCallback(
    async (
      types: Record<string, Array<{ name: string; type: string }>>,
      primaryType: string,
      message: Record<string, unknown>
    ): Promise<string | null> => {
      if (!wallet) {
        return null;
      }

      try {
        // Chainless: no network check, no switch, for every wallet kind.
        const prepared = await prepareWallet(wallet, ACTIONS.walletRelations());
        return await signPreparedTypedData(prepared, {
          types: { EIP712Domain: EIP712_DOMAIN_NAME_VERSION, ...types },
          primaryType,
          domain: WALLET_RELATIONS_EIP712_DOMAIN,
          message,
        });
      } catch {
        return null;
      }
    },
    [wallet]
  );

  /**
   * Query linked wallets using TanStack Query
   * Auto-fetches when Privy embedded wallet is connected
   */
  const {
    data: linkedWallets = [],
    isLoading: isQueryLoading,
    error: queryError,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: ['wallet-relations', address],
    queryFn: async (): Promise<LinkedWallet[]> => {
      if (!address || !wallet) return [];

      // 1. Fetch current nonce
      const noncesResult = await getWalletNonces(address);
      if (!noncesResult.success) {
        throw new Error(noncesResult.error);
      }
      const nonce = parseInt(noncesResult.data.queryNonce, 10);

      // 2. Create timestamp and sign query with nonce
      const timestamp = Math.floor(Date.now() / 1000);
      const signature = await signTypedData(
        { QueryRelations: [...EIP712_TYPES.QueryRelations] },
        'QueryRelations',
        {
          wallet: address,
          timestamp: BigInt(timestamp).toString(),
          nonce: BigInt(nonce).toString(),
        }
      );

      if (!signature) {
        throw new Error('Failed to sign query request');
      }

      // 3. Submit query with nonce
      const result = await queryLinkedWallets({
        wallet: address,
        timestamp,
        nonce,
        signature,
      });

      if (result.success) {
        return result.data;
      }

      // No relations is not an error - return empty array
      if (result.error.includes('No relations')) {
        return [];
      }

      // Handle nonce mismatch - can retry with fresh nonce
      if (result.error.includes('Invalid nonce')) {
        throw new Error(result.error);
      }

      throw new Error(result.error);
    },
    // Only fetch when user has explicitly opted in (to avoid auto-triggering 2FA popup)
    enabled: !!address && !!wallet && canLinkWallets && hasFetched,
    staleTime: 1000 * 60 * 5, // 5 minutes - avoid re-signing too often
    retry: (failureCount, error) => {
      // Retry once on nonce mismatch
      if (error instanceof Error && error.message.includes('Invalid nonce') && failureCount < 1) {
        return true;
      }
      return false;
    },
  });

  /**
   * Sign a link request for the current wallet
   * Returns signature that can be sent to another wallet user
   */
  const signLinkRequest = useCallback(
    async (otherWalletAddress: string): Promise<string | null> => {
      if (!address) {
        return null;
      }

      // Get canonical order
      const { walletA, walletB } = canonicalOrder(address, otherWalletAddress);

      return signTypedData({ LinkWallets: [...EIP712_TYPES.LinkWallets] }, 'LinkWallets', {
        walletA,
        walletB,
      });
    },
    [address, signTypedData]
  );

  /**
   * Sign an unlink request for the current wallet
   * Returns signature and the timestamp/nonce used for signing
   */
  const signUnlinkRequest = useCallback(async (): Promise<SignUnlinkResult | null> => {
    if (!address) {
      return null;
    }

    // 1. Fetch current nonce
    const noncesResult = await getWalletNonces(address);
    if (!noncesResult.success) {
      return null;
    }
    const nonce = parseInt(noncesResult.data.unlinkNonce, 10);

    // 2. Create timestamp
    const timestamp = Math.floor(Date.now() / 1000);

    // 3. Sign with timestamp and nonce
    const signature = await signTypedData(
      { UnlinkWallet: [...EIP712_TYPES.UnlinkWallet] },
      'UnlinkWallet',
      {
        wallet: address,
        timestamp: BigInt(timestamp).toString(),
        nonce: BigInt(nonce).toString(),
      }
    );

    if (!signature) {
      return null;
    }

    return { signature, timestamp, nonce };
  }, [address, signTypedData]);

  /**
   * Link wallet mutation
   */
  const linkMutation = useMutation({
    mutationFn: async ({
      otherWalletAddress,
      otherWalletSignature,
    }: {
      otherWalletAddress: string;
      otherWalletSignature: string;
    }) => {
      if (!address) throw new Error('No wallet connected');
      if (!wallet) throw new Error('No wallet connected');

      // Sign for the current wallet
      const currentSignature = await signLinkRequest(otherWalletAddress);
      if (!currentSignature) throw new Error('Failed to sign link request');

      // Get canonical order to determine which signature is A and B
      const { walletA, walletB } = canonicalOrder(address, otherWalletAddress);
      const signatureA =
        address.toLowerCase() === walletA.toLowerCase() ? currentSignature : otherWalletSignature;
      const signatureB =
        address.toLowerCase() === walletA.toLowerCase() ? otherWalletSignature : currentSignature;

      const result = await apiLinkWallets({
        walletA,
        walletB,
        signatureA,
        signatureB,
      });

      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      // Invalidate to refetch linked wallets
      void queryClient.invalidateQueries({ queryKey: ['wallet-relations', address] });
    },
  });

  /**
   * Unlink wallet mutation
   */
  const unlinkMutation = useMutation({
    mutationFn: async () => {
      if (!address) throw new Error('No wallet connected');

      const signResult = await signUnlinkRequest();
      if (!signResult) throw new Error('Failed to sign unlink request');

      const result = await apiUnlinkWallet({
        wallet: address,
        timestamp: signResult.timestamp,
        nonce: signResult.nonce,
        signature: signResult.signature,
      });

      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      // Invalidate to refetch (will return empty array)
      void queryClient.invalidateQueries({ queryKey: ['wallet-relations', address] });
    },
  });

  /**
   * Rename wallet mutation
   */
  const renameMutation = useMutation({
    mutationFn: async ({
      targetAddress,
      newLabel,
    }: {
      targetAddress: string;
      newLabel: string;
    }) => {
      if (!address) throw new Error('No wallet connected');

      const timestamp = Math.floor(Date.now() / 1000);
      const signature = await signTypedData({ SetLabel: [...EIP712_TYPES.SetLabel] }, 'SetLabel', {
        wallet: address,
        targetWallet: targetAddress,
        label: newLabel,
        timestamp: BigInt(timestamp).toString(),
      });

      if (!signature) throw new Error('Failed to sign label request');

      const result = await apiSetWalletLabel({
        wallet: address,
        targetWallet: targetAddress,
        label: newLabel,
        timestamp,
        signature,
      });

      if (!result.success) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['wallet-relations', address] });
    },
  });

  /**
   * Link another wallet to the current wallet
   * Requires signature from the other wallet
   */
  const linkWallet = useCallback(
    async (otherWalletAddress: string, otherWalletSignature: string): Promise<boolean> => {
      try {
        await linkMutation.mutateAsync({ otherWalletAddress, otherWalletSignature });
        return true;
      } catch {
        return false;
      }
    },
    [linkMutation]
  );

  /**
   * Unlink the current wallet from all relations
   */
  const unlinkAll = useCallback(async (): Promise<boolean> => {
    try {
      await unlinkMutation.mutateAsync();
      return true;
    } catch {
      return false;
    }
  }, [unlinkMutation]);

  /**
   * Rename a linked wallet (set its label)
   */
  const renameWallet = useCallback(
    async (targetAddress: string, newLabel: string): Promise<boolean> => {
      try {
        await renameMutation.mutateAsync({ targetAddress, newLabel });
        return true;
      } catch {
        return false;
      }
    },
    [renameMutation]
  );

  /**
   * Fetch linked wallets (triggers signing, which may show 2FA prompt)
   * Call this when user explicitly wants to load their linked wallets
   */
  const fetchLinkedWallets = useCallback(async (): Promise<void> => {
    setHasFetched(true);
    // Query will auto-run once hasFetched is true due to enabled condition
  }, []);

  /**
   * Refetch wrapper that matches the expected return type
   */
  const refetch = useCallback(async (): Promise<void> => {
    if (!hasFetched) {
      setHasFetched(true);
    }
    await queryRefetch();
  }, [queryRefetch, hasFetched]);

  // Combine errors from query and mutations
  const error =
    queryError?.message ??
    linkMutation.error?.message ??
    unlinkMutation.error?.message ??
    renameMutation.error?.message ??
    null;

  return {
    linkedWallets,
    isLoading:
      isQueryLoading ||
      linkMutation.isPending ||
      unlinkMutation.isPending ||
      renameMutation.isPending,
    error,
    isPrivyEmbeddedWallet,
    canLinkWallets,
    hasFetched,
    fetchLinkedWallets,
    refetch,
    linkWallet,
    unlinkAll,
    signLinkRequest,
    signUnlinkRequest,
    renameWallet,
  };
}
